import { Box, Link, Typography } from '@mui/material'
import { type FC, type ReactNode, useState } from 'react'

const drawerWidth = 480
const iconStripWidth = 40

export interface FilterTab {
    icon: ReactNode
    label: string
    content: ReactNode
    onClear?: () => void
}

interface FiltersDrawerProps {
    tabs: FilterTab[]
    children?: ReactNode
}

export const FiltersDrawer: FC<FiltersDrawerProps> = ({ tabs, children }) => {
    const [activeTab, setActiveTab] = useState<number | null>(null)
    const isOpen = activeTab !== null
    const current = isOpen ? tabs[activeTab] : null

    const handleTabClick = (i: number) => {
        setActiveTab(i === activeTab ? null : i)
    }

    return (
        <Box
            sx={{
                width: isOpen ? drawerWidth : iconStripWidth,
                minWidth: isOpen ? drawerWidth : iconStripWidth,
                transition:
                    'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    width: drawerWidth,
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                <Box
                    sx={{
                        width: iconStripWidth,
                        minWidth: iconStripWidth,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        pt: 0.5,
                        gap: 0.25,
                    }}
                >
                    {tabs.map((tab, i) => (
                        <Box
                            key={tab.label}
                            title={tab.label}
                            data-testid={`filter-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => handleTabClick(i)}
                            sx={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                cursor: 'pointer',
                                color:
                                    i === activeTab
                                        ? 'primary.main'
                                        : 'text.secondary',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            {tab.icon}
                        </Box>
                    ))}
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            height: 56,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography fontSize="0.8125rem" fontWeight={600}>
                            {current?.label}
                        </Typography>
                        {current?.onClear && (
                            <Link
                                component="button"
                                onClick={current.onClear}
                                sx={{
                                    fontSize: '0.6875rem',
                                    color: 'text.secondary',
                                    textDecoration: 'none',
                                    '&:hover': { color: 'primary.main' },
                                }}
                            >
                                Clear
                            </Link>
                        )}
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {children}
                        {current?.content}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
